import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

/**
 * Hook to handle unsaved changes detection and confirmation
 * @param isDirty Whether form has unsaved changes
 * @param onCancel Callback to execute when confirming discard
 * @returns Functions and state for managing unsaved changes
 */
const useUnsavedChanges = (isDirty: boolean, onCancel: () => void) => {
  const router = useRouter();
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  // Set up browser navigation interception
  useEffect(() => {
    const confirmationMessage = 'Changes you made may not be saved.';
    
    // Handle browser navigation or tab closing
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      
      e.preventDefault();
      return confirmationMessage;
    };
    
    // Handle Next.js navigation
    const beforeRouteHandler = (url: string) => {
      if (!isDirty) return;
      
      if (router.pathname !== url) {
        setPendingUrl(url);
        setShowUnsavedChangesAlert(true);
        router.events.emit('routeChangeError');
      }
    };
    
    // Add event listeners
    if (isDirty) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      router.events.on('routeChangeStart', beforeRouteHandler);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', beforeRouteHandler);
    }
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', beforeRouteHandler);
    };
  }, [isDirty, router]);

  // Handle user canceling form
  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedChangesAlert(true);
    } else {
      onCancel();
    }
  };

  // Handle discarding changes confirmation
  const handleDiscardChanges = () => {
    setShowUnsavedChangesAlert(false);
    
    if (pendingUrl) {
      router.push(pendingUrl);
      setPendingUrl(null);
    }
    
    onCancel();
  };

  return {
    showUnsavedChangesAlert,
    handleCancel,
    handleDiscardChanges,
    closeAlert: () => setShowUnsavedChangesAlert(false)
  };
};

export default useUnsavedChanges; 